import { NextSeo } from 'next-seo';

import { CrehanaTFunction, useTranslation } from '@crehana/i18n';
import ChevronDown from '@crehana/icons/fill/ChevronDown';
import Browser from '@crehana/icons/pictograms/Browser';
import Discount from '@crehana/icons/pictograms/Discount';
import LaptopPremium from '@crehana/icons/pictograms/LaptopPremium';
import { CustomNextImage as Image } from '@crehana/next';
import { CountryPrefix } from '@crehana/ts-types';
import TextLink from '@crehana/ui/dist/Buttons/TextLink';
import { Col, Container, Row } from '@crehana/ui/dist/GridV2';
import MainFooter from '@crehana/web/MainFooter';

import LightMenu from '@/shared/components/MainMenu/LightMenu';
import { initializeApollo } from '@/shared/libs/apollo';
import getBaseUrl from '@/shared/utils/getBaseUrl';
import { CustomGetServerSideProps } from '@/shared/utils/getServerSideProps';
import { getHrefLangData } from '@/shared/utils/seo';
import { AppProps } from '@/shared/utils/types';
import usePageView from '@/shared/utils/UserActivity';

import { CouponCard, ShareLinks } from './components';
import { COUPONS_LANDING_AMPLITUDE_PAGE_NAME } from './constants';
import seoData from './CouponsLanding.data';
import GET_COUPONS from './graphql/Coupons.graphql';
import { couponLandingQuery as Coupons } from './graphql/types/couponLandingQuery';
import FAQSection from './sections/FAQSection';
import { Coupon } from './types';

const scrollTo = (DOMNodeId: string) => {
  const DOMNode = document.getElementById(DOMNodeId);

  if (DOMNode) {
    window.scrollTo({
      top: DOMNode.offsetTop,
      behavior: 'smooth',
    });
  }
};

const getSteps = (
  t: CrehanaTFunction,
): { id: number; text: string; icon: React.ReactElement }[] => [
  {
    id: 1,
    text: t('STEP_ONE', 'Copia el código de cupón que quieras usar'),
    icon: <Discount size={48} color="#4B22F4" secondaryColor="#181B32" />,
  },
  {
    id: 2,
    text: t(
      'STEP_TWO',
      'Ingresa a "Aplícalo aquí” para elegir tu membresía o cursos',
    ),
    icon: <LaptopPremium size={48} color="#4B22F4" secondaryColor="#181B32" />,
  },
  {
    id: 3,
    text: t('STEP_THREE', 'Aplica el código en la pantalla de pago y ¡listo!'),
    icon: <Browser size={48} color="#4B22F4" secondaryColor="#181B32" />,
  },
];

type CouponsLandingProps = {
  coupons: Coupon[];
};

const CouponsLanding: React.FC<AppProps<CouponsLandingProps>> = ({
  coupons = [],
  countryPrefix,
}) => {
  usePageView({ type: 'page' });
  const { t } = useTranslation();
  const SITE_URL_WITH_PREFIX = getBaseUrl(countryPrefix as string);

  return (
    <>
      <NextSeo
        title="Cupones de descuento | Códigos de promociones | Crehana"
        description={seoData.og_data.description}
        canonical={`${SITE_URL_WITH_PREFIX}/cupones/`}
        openGraph={{
          url: `${SITE_URL_WITH_PREFIX}/cupones`,
          title: `${seoData.og_data.title} | ${
            countryPrefix ? countryPrefix.toUpperCase() : ''
          }`,
          description: seoData.og_data.description,
          type: seoData.og_data.type,
          site_name: seoData.og_data.site_name,
        }}
        additionalMetaTags={[{ property: 'lang', content: 'es' }]}
        languageAlternates={[
          ...getHrefLangData({
            currentCountryPrefix: countryPrefix || '',
            url: `/cupones/`,
          }),
          {
            href: `${getBaseUrl()}/br/cupones`,
            hrefLang: 'pt-br',
          },
        ]}
      />
      <section className="overflow-x-hidden" style={{ background: '#551BF9' }}>
        <div className="relative">
          <LightMenu
            isTransparent
            theme="dark"
            countryPrefix={countryPrefix || ''}
            amplitudePageName={COUPONS_LANDING_AMPLITUDE_PAGE_NAME}
          />
        </div>
        <Container className="relative pb-48 sm:pb-72 md:pb-120 pt-104 sm:pt-112 md:pt-120">
          <Row className="relative z-3">
            <Col sm={6} md={8}>
              <h1 className="font-h1-md text-white">
                {t(
                  'HERO_SECTION_TITLE',
                  'Cupones de descuento Crehana para aprender online',
                )}
              </h1>
              <p className="font-body1-md mt-16 md:24 text-white">
                {t(
                  'HERO_SECTION_SUBTITLE',
                  'Desarrolla tu potencial con las mejores promociones.',
                )}
              </p>
              <TextLink
                onClick={() => {
                  scrollTo('coupons-section');
                }}
                label={t('HERO_SECTION_LINK', 'Descúbrelos')}
                className="text-secondary-main mt-32 hidden md:flex"
                leftIcon={{
                  component: ChevronDown,
                  size: 24,
                }}
              />
            </Col>
          </Row>
          <div
            className="w-full absolute bottom-0 flex justify-end"
            style={{ marginLeft: '100px' }}
          >
            <Image
              src={
                'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-adedd3c6-9fad-47b0-854d-c3820f77b3ad-student.png?auto=format'
              }
              alt="Fotografía de estudiante"
              loader={({ src }) => src}
              layout="intrinsic"
              quality={100}
              width={720}
              height={378}
            />
          </div>
        </Container>
      </section>
      <section id="coupons-section" className="py-40 pt-32 sm:pt-52 md:pt-96">
        <Container>
          <div className="sm:w-10/12 mx-auto mb-32 sm:mb-48 md:mb-64">
            <h1 className="font-h2-md text-center ">
              {t('COUPONS_SECTION_TITLE', 'Elige el cupón para ti')}
            </h1>
          </div>
          <Row className="items-start">
            <Col sm={1} className="hidden sm:inline-flex flex-col">
              <ShareLinks />
            </Col>
            <>
              {coupons.map(coupon => (
                <Col key={coupon.id} className="mb-16 sm:mb-0" xs={12} sm={5}>
                  <CouponCard coupon={coupon} />
                </Col>
              ))}
            </>
          </Row>
        </Container>
      </section>
      <section className="py-40 md:py-64">
        <Container>
          <div className="md:w-10/12 mx-auto">
            <h2 className="font-h2-md text-center mb-80 sm:mb-52 md:mb-96">
              {t(
                'HOW_TO_SECTION_TITLE',
                '¿Cómo usar tu cupón de descuento Crehana?',
              )}
            </h2>
            <Row>
              {getSteps(t).map((el, index) => (
                <Col
                  key={el.id}
                  xs={12}
                  sm={4}
                  className={`${index !== 0 ? 'mt-32 sm:mt-0' : ''}`}
                >
                  <article className="flex flex-col items-center sm:max-w-280 sm:mx-auto sm:px-8">
                    {el.icon}
                    <h5 className="text-center mt-16 font-h4-md">{el.text}</h5>
                  </article>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>
      <FAQSection
        handleScrollTo={() => {
          scrollTo('coupons-section');
        }}
      />
      <MainFooter />
    </>
  );
};

export const getServerSideProps: CustomGetServerSideProps<
  CouponsLandingProps
> = async ({ query }) => {
  const countryPrefix = (query.country as CountryPrefix) || null;
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query<Coupons>({
    query: GET_COUPONS,
  });

  return {
    props: {
      countryPrefix,
      coupons:
        data.membershipCouponData && data.paygCouponData
          ? [
              {
                id: 1,
                title: `${data.membershipCouponData.value} % de descuento en Membresía Premium`,
                description:
                  'Elige tu plan para acceder a todos los cursos, especializaciones y mentorías.',
                code: data.membershipCouponData.code,
                percentageOff: data.membershipCouponData.value,
                productUrl: data.membershipCouponData.link,
                image:
                  'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-ac2b3879-6fdf-4f6b-bc14-b59c67e4ce31-membership.jpg?auto=format',
              },
              {
                id: 2,
                title: `${data.paygCouponData.value} % de descuento en todos los cursos`,
                description:
                  'Explora entre 16 categorías y elige los cursos que quieras aprender hoy.',
                code: data.paygCouponData.code,
                percentageOff: data.paygCouponData.value,
                productUrl: data.paygCouponData.link,
                image:
                  'https://crehana-vercel-images-prd.imgix.net/public/crehana-frontend-mf/automated-v2-79806ef7-985a-4ae3-93ad-0dae382f3c0b-payg.jpg?auto=format',
              },
            ]
          : [],
    },
  };
};

export default CouponsLanding;
